/**
 * ProposalsSubTab: list of commercial proposals for this property.
 *
 * Ported from the Proposals section of DocumentsTab on 2026-05-29 as
 * part of the Documents-tab dissolution. Read flow + rendering kept
 * intact; only the import paths and the standalone wrapper changed.
 */

import { useCallback, useEffect, useState } from 'react';
import { formatDateMedium } from '../../../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import LoadingSpinner from '../../../../../../components/LoadingSpinner';
import { StatusBadge } from '../../../../../../components/DataTable';
import { PdfModal } from '../../../../../../components/PdfModal';
import { signPosDocument } from '../../../../../../lib/posDocuments';
import { useConfig } from '../../../../../../contexts/ConfigContext';
import { POS_GOLD } from '../../constants';
import type { EnrichedProperty } from '../types';

interface Proposal {
  id: string;
  reference_code: string;
  access_key?: string | null;
  status: string | null;
  selected_tier: string | null;
  annual_total: number | null;
  currency_code: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  accepted_at: string | null;
  expires_at: string | null;
  signed_pdf_url: string | null;
  accepted_pdf_path: string | null;
  is_locked: boolean | null;
  created_at: string;
}

interface ProposalsSubTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return formatDateMedium(s);
}

function proposalStatusBadge(p: Proposal): { label: string; bg: string; color: string } {
  const s = p.status || 'draft';
  if (p.accepted_at || s === 'accepted') return { label: 'Accepted', bg: 'var(--color-success-bg)', color: 'var(--color-success)' };
  if (s === 'sent') return { label: 'Sent', bg: 'var(--color-info-bg)', color: 'var(--color-info-text)' };
  if (s === 'viewed') return { label: 'Viewed', bg: 'var(--color-info-bg)', color: 'var(--color-info-text)' };
  if (s === 'rejected') return { label: 'Rejected', bg: 'var(--color-error-bg)', color: 'var(--color-error)' };
  if (s === 'expired') return { label: 'Expired', bg: 'var(--color-error-bg)', color: 'var(--color-error)' };
  if (s === 'draft') return { label: 'Draft', bg: 'var(--color-bg)', color: 'var(--color-text-light)' };
  return { label: s, bg: 'var(--color-border)', color: 'var(--color-text-light)' };
}

export default function ProposalsSubTab({ supabase, property }: ProposalsSubTabProps) {
  const { countryCode, formatCurrency } = useConfig();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('pos_proposals')
      .select('id, reference_code, status, selected_tier, annual_total, currency_code, sent_at, viewed_at, accepted_at, expires_at, signed_pdf_url, accepted_pdf_path, is_locked, created_at, access_key')
      .eq('property_id', property.id)
      .order('created_at', { ascending: false });
    setProposals((data as Proposal[]) || []);
    setLoading(false);
  }, [supabase, property.id]);

  useEffect(() => { void load(); }, [load]);

  function copy(url: string, label: string) {
    navigator.clipboard.writeText(url).then(() => {
      setCopyFeedback(label);
      setTimeout(() => setCopyFeedback(null), 2000);
    });
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><LoadingSpinner /></div>;

  const cc = (countryCode || 'za').toLowerCase();

  if (proposals.length === 0) {
    return (
      <div className="text-muted fs-sm" style={{ padding: '1.5rem 0' }}>
        No proposals linked to this property yet.
      </div>
    );
  }

  return (
    <div>
      {copyFeedback && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', right: '1.5rem',
          background: 'var(--color-success)', color: 'var(--color-white)', padding: '0.5rem 1rem',
          borderRadius: 'var(--radius-md)', fontSize: 'var(--text-base)', zIndex: 2000,
        }}>
          {copyFeedback}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {proposals.map((p, idx) => {
          const badge = proposalStatusBadge(p);
          // With the secret code, without which the page shows no contact details (26 Oct 2026).
          const key = p.access_key ? `&k=${encodeURIComponent(p.access_key)}` : '';
          const proposalUrl = `https://peaceofsleep.vip/p/?ref=${p.reference_code}&c=${cc}${key}`;
          const agreementUrl = `https://peaceofsleep.vip/${cc}/agreement/?ref=${p.reference_code}${key}`;
          return (
            <div key={p.id} style={{
              padding: '1rem 1.25rem',
              borderBottom: idx < proposals.length - 1 ? '1px solid var(--color-border)' : 'none',
              display: 'flex', flexDirection: 'column', gap: '0.75rem',
            }}>
              <div className="flex items-start justify-between flex-wrap gap-lg">
                <div className="flex items-center flex-wrap gap-md">
                  <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 'var(--text-lg)', color: POS_GOLD }}>
                    {p.reference_code}
                  </span>
                  <StatusBadge bg={badge.bg} color={badge.color}>{badge.label}</StatusBadge>
                  {p.is_locked && (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)' }} title="Locked after customer acceptance">🔒 Locked</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 'var(--text-sm)', padding: '0.375rem 0.75rem' }}
                    onClick={() => copy(proposalUrl, 'Proposal link copied')}>
                    Copy proposal link
                  </button>
                  <button type="button" className="btn btn-secondary" style={{ fontSize: 'var(--text-sm)', padding: '0.375rem 0.75rem' }}
                    onClick={() => copy(agreementUrl, 'Agreement link copied')}>
                    Copy agreement link
                  </button>
                  {p.signed_pdf_url && (
                    <button type="button" onClick={async () => {
                      const url = await signPosDocument(supabase, p.signed_pdf_url!);
                      if (!url) {
                        alert('Could not load the signed PDF.');
                        return;
                      }
                      setPdfPreview({ url, title: 'Signed Proposal' });
                    }}
                      className="btn btn-primary" style={{ fontSize: 'var(--text-sm)', padding: '0.375rem 0.75rem' }}>
                      View signed PDF
                    </button>
                  )}
                  {p.accepted_pdf_path && (
                    <button type="button" className="btn btn-primary" style={{ fontSize: 'var(--text-sm)', padding: '0.375rem 0.75rem' }}
                      onClick={async () => {
                        const { data: signed, error } = await supabase.storage
                          .from('pos-documents')
                          .createSignedUrl(p.accepted_pdf_path!, 3600);
                        if (error || !signed?.signedUrl) {
                          alert('Could not load the accepted-proposal PDF. ' + (error?.message || ''));
                          return;
                        }
                        setPdfPreview({ url: signed.signedUrl, title: 'Accepted Proposal (Locked)' });
                      }}>
                      View accepted PDF
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem 1.5rem', fontSize: 'var(--text-md)' }}>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Tier</div>
                  <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{p.selected_tier || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Annual total</div>
                  <div className="fw-500">{p.annual_total != null ? formatCurrency(p.annual_total) : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Sent</div>
                  <div className="fw-500">{formatDate(p.sent_at)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Accepted</div>
                  <div className="fw-500">{formatDate(p.accepted_at)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Expires</div>
                  <div className="fw-500">{formatDate(p.expires_at)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {pdfPreview && (
        <PdfModal url={pdfPreview.url} title={pdfPreview.title} onClose={() => setPdfPreview(null)} />
      )}
    </div>
  );
}
